/**
 * LA GIOCATA E LA PRENOTAZIONE SU CARTA: le pagine da mandare alla stampante.
 *
 * Qui si compone soltanto: una giocata (come la da' ticketsApi.riga) entra,
 * una pagina HTML intera esce, e la manda in stampa finestraDiStampa. Niente
 * DOM e niente chiamate: la stessa giocata da' sempre lo stesso foglio, e le
 * prove lo leggono come testo.
 *
 * TRE FOGLI
 *   - il ROTOLO (80 o 58 mm), il «promemoria» scelto dal proprietario
 *     (Rotolo A): fasce nere, codice, cliente, data, eventi uno sotto
 *     l'altro, i numeri in fondo e il gioco responsabile;
 *   - l'A4, la copia della giocata per chi stampa a casa;
 *   - la PRENOTAZIONE, rotolo o A4: codice, collegamento scritto per intero,
 *     eventi e, se c'e', l'importo proposto.
 *
 * IL LOGO DELLA SKIN, su ogni carta, se chi stampa lo vuole e il servizio lo
 * concede (la regola del backoffice: SEMPRE, MAI, SCEGLIE_CHI_STAMPA). Sul
 * rotolo sta in cima, al centro, in bianco e nero: la stampante termica il
 * colore non ce l'ha. Senza logo, al suo posto il dominio scritto.
 *
 * LE REGOLE DI SCRITTURA, le stesse dello schermo:
 *   - gli importi con formattaImporto nella valuta della skin, mai «€»;
 *   - le ore nel fuso di chi STAMPA (fuso(): la sua scelta, se no quello
 *     della skin, se no quello del browser) e col nome del fuso accanto: su
 *     un foglio non si puo' chiedere a chi legge in che fuso stava il browser;
 *   - le quote nel formato di chi stampa (mostraQuota);
 *   - ogni parola da i18next.t, con la frase italiana come chiave;
 *   - ogni valore da pulisci(), anche dentro gli attributi.
 *
 * COSA NON STAMPA
 *   - il nome dello sport: la giocata ha solo sportId e il sito non ha un
 *     elenco da cui leggerne il nome (sul promemoria c'e' il campionato);
 *   - un pezzo di identificativo al posto del cliente: o il nome, o «—»;
 *   - un numero di copia inventato: la copia la conta il servizio.
 */
import i18next from 'i18next';
import {formattaOra, formattaImporto, mostraQuota} from './formato';
import {fuso as fusoDiChiGuarda, lingua} from '../preferenze';
import {skinCorrente, valuta} from '../skin';
import {etichettaStato, typeLabels} from './statiGiocata';
import {getCurrentUsername} from './currentUser';
import {indirizzoImmagine} from './immagini';
import {pulisci} from './finestraDiStampa';
import {testiStampa} from './testiLegali';

const t = (chiave, valori) => i18next.t(chiave, valori);

/* GLI STATI CHE SI STAMPANO E SI CONDIVIDONO: in corso, liquidata, da
   riscuotere, pagata, annullata, venduta. Gli altri no. DA_ACCETTARE e
   RIPROPOSTA non sono ancora giocate; IN_CASHOUT e IN_ANNULLO durano pochi
   secondi e l'esito arriva subito dopo; RIFIUTATA, SCADUTA, IN_RIFIUTO e
   IN_REGISTRAZIONE non sono mai partite. E' lo stesso elenco del servizio
   (TicketStatus.STAMPABILI), che rifiuta gli altri con 409. */
export const STATI_STAMPABILI = ['PENDING', 'SETTLED', 'AWAITING_PAYMENT', 'PAID', 'VOIDED', 'VENDUTA'];

export function stampabile(stato) {
  return STATI_STAMPABILI.includes(stato);
}

export const FORMATI = ['A4', 'ROTOLO_80', 'ROTOLO_58'];

function formatoDi(f) {
  return FORMATI.includes(f) ? f : 'A4';
}

// ---------------------------------------------------------------------------
// I pezzi della scrittura
// ---------------------------------------------------------------------------

function numeroOppureNull(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Le maiuscole delle fasce, nella lingua di chi stampa. */
export function maiuscole(testo) {
  try {
    return String(testo || '').toLocaleUpperCase(lingua());
  } catch (e) {
    return String(testo || '').toUpperCase();
  }
}

/** Un importo con il simbolo della valuta della skin. */
function soldi(n, codice) {
  return formattaImporto(n, {valuta: codice});
}

/* La cifra senza il simbolo, coi decimali della valuta: serve nelle celle
   strette della tabella del sistema, dove il simbolo e' gia' in alto. */
function cifra(n, codice) {
  try {
    return new Intl.NumberFormat(lingua(), {style: 'currency', currency: codice})
        .formatToParts(Number(n || 0))
        .filter((p) => p.type !== 'currency')
        .map((p) => p.value).join('').trim();
  } catch (e) {
    return Number(n || 0).toFixed(2);
  }
}

/** Data e ora complete, col nome del fuso: «2/11/2026 19:58 CET». */
function quandoConFuso(iso, zona) {
  return formattaOra(iso, 'completa', {fuso: zona, sigla: true});
}

/** La sigla di un fuso in un momento (CET d'inverno, CEST d'estate). */
function siglaDi(zona, quando) {
  try {
    const parti = new Intl.DateTimeFormat(lingua(), {timeZone: zona, timeZoneName: 'short'})
        .formatToParts(quando instanceof Date ? quando : new Date(quando || Date.now()));
    const p = parti.find((x) => x.type === 'timeZoneName');
    return p ? p.value : '';
  } catch (e) {
    return '';
  }
}

function notaOrari(zona, quando) {
  const sigla = siglaDi(zona, quando);
  return sigla
    ? t('Orari nel fuso {{fuso}} ({{sigla}}).', {fuso: zona, sigla})
    : t('Orari nel fuso {{fuso}}.', {fuso: zona});
}

/* LE PAROLE DELLE GRANDEZZE sono quelle della schedina e del dettaglio:
   «Singole, Doppie, Triple», poi «4-uple». */
function nomeGrandezza(k) {
  if (k === 1) return t('Singole');
  if (k === 2) return t('Doppie');
  if (k === 3) return t('Triple');
  return t('{{n}}-uple', {n: k});
}

/** Il segno come lo scrive il dettaglio: il nome, e la linea (sbv) se non e' gia' nel nome. */
function segnoDi(m) {
  const base = m.selectionName || m.selectionCode || '-';
  const sbv = m.sbv == null ? '' : String(m.sbv);
  if (sbv && sbv !== '0' && !String(m.selectionName || m.selectionCode || '').includes(sbv)) {
    return `${base} (${sbv})`;
  }
  return base;
}

/* LA QUOTA DI UNA SELEZIONE E' value, NON odd: value e' il prezzo con cui si
   calcola la vincita (lo stesso ragionamento di MyBets, quotaSelezione). */
function quotaDi(m) {
  if (m && m.value != null) return {testo: mostraQuota(m.value) || '-', valore: Number(m.value)};
  if (m && m.odd != null) return {testo: mostraQuota(m.odd) || '-', valore: Number(m.odd)};
  return {testo: '-', valore: null};
}

/** «—» invece di un pezzo di identificativo: il cliente o ha un nome, o non si scrive. */
function clienteDi(giocata, io, nomeUtente) {
  const id = giocata.userId;
  if (id && io && id === io) return getCurrentUsername() || '\u2014';
  if (id && typeof nomeUtente === 'function') {
    const nome = nomeUtente(id);
    const ripiego = `${String(id).slice(0, 8)}\u2026`;
    if (nome && nome !== ripiego && nome !== '-' && nome !== id) return String(nome);
  }
  return '\u2014';
}

// ---------------------------------------------------------------------------
// La giocata, normalizzata una volta per carta, immagine e messaggio
// ---------------------------------------------------------------------------

/**
 * Quello che si stampa di una giocata, gia' deciso: lo leggono il rotolo,
 * l'A4, l'immagine da condividere e il messaggio (condividi.js).
 *
 * @param giocata una riga di ticketsApi (il dettaglio, con selezioni e system)
 * @param io      chi stampa (getCurrentUserId)
 * @param nomeUtente il nome di un utente della rete (MyBets.nomeUtente)
 */
export function datiStampa(giocata, {io, nomeUtente} = {}) {
  const g = giocata || {};
  const tipo = g.type || '';
  const righe = Array.isArray(g.system) ? g.system.slice() : [];
  righe.sort((a, b) => (Number(a.combination) || 0) - (Number(b.combination) || 0));

  const sistema = righe.map((r) => {
    const grandezza = Number(r.combination) || 0;
    const combinazioni = Number(r.multiples) || 0;
    const totale = Number(r.amount) || 0;
    /* LA MOLTIPLICAZIONE SI SCRIVE SOLO QUANDO E' VERA: se i centesimi non si
       dividono esatti le colonne non costano tutte uguale (MyBets, dettaglio
       sistema), e «6 × 1,00» su colonne da 0,99 e 1,01 sarebbe falso. */
    const diviso = combinazioni ? totale / combinazioni : 0;
    const colonnaMin = r.importoColonnaMin != null ? Number(r.importoColonnaMin) : diviso;
    const colonnaMax = r.importoColonnaMax != null ? Number(r.importoColonnaMax) : diviso;
    return {
      grandezza,
      nome: nomeGrandezza(grandezza),
      combinazioni,
      totale,
      colonnaMin,
      colonnaMax,
      unSoloImporto: colonnaMin === colonnaMax,
      vincitaMin: numeroOppureNull(r.vincitaMin),
      vincitaMax: numeroOppureNull(r.vincitaMax),
    };
  });

  const grandezze = tipo === 'SYSTEM' ? sistema.map((r) => r.nome) : [];
  const base = typeLabels[tipo] || tipo || '';
  const tipoNome = grandezze.length ? `${base} \u00b7 ${grandezze.join(', ')}` : base;

  const selezioni = Array.isArray(g.selezioni) && g.selezioni.length
    ? g.selezioni : (Array.isArray(g.matches) ? g.matches : []);
  const eventi = selezioni.map((m) => {
    const q = quotaDi(m);
    return {
      competizione: m.competitionName || '',
      inizio: m.startsAt || m.matchDate || null,
      numero: m.eventNumber != null ? String(m.eventNumber) : '',
      evento: m.eventName || m.matchName || '',
      mercato: m.marketName || m.marketKey || '-',
      segno: segnoDi(m),
      quota: q.testo,
      valore: q.valore,
    };
  });
  const quanteEventi = Number(g.eventsCount)
    || new Set(selezioni.map((m) => m.eventId).filter(Boolean)).size || eventi.length;

  const combinazioni = Number(g.combinazioni) || 1;
  const piuColonne = tipo === 'SYSTEM' || tipo === 'INTEGRAL' || combinazioni > 1;
  const quotaMin = Number(g.oddsMin) > 0 ? Number(g.oddsMin) : null;
  const quotaMax = Number(g.oddsMax) > 0 ? Number(g.oddsMax) : null;
  const vincitaMin = Number(g.winColMin) || 0;
  /* winColMax null = non si sa quanto paga UNA colonna: si scrive «—», mai il
     totale di tutte spacciato per una (ticketsApi, massimoColonna). */
  /* Quanto costa una colonna: il dettaglio lo manda (importoColonnaMin e
     Max); zero vuol dire che non si sa, e allora si scrive solo quante. */
  const colonnaMin = Number(g.amountCombMin) > 0 ? Number(g.amountCombMin) : null;
  const colonnaMax = Number(g.amountCombMax) > 0 ? Number(g.amountCombMax) : null;
  const integrale = tipo === 'INTEGRAL';
  /* L'INTEGRALE: il servizio non manda quanto paga al massimo una colonna,
     ma lo si sa lo stesso, ed e' la quota piu' alta per quello che costa la
     colonna. «Se vincono tutte» invece non c'e': i segni dello stesso
     evento si escludono, vince una colonna sola. */
  let vincitaMax = g.winColMax == null ? null : Number(g.winColMax);
  if (vincitaMax == null && integrale && quotaMax != null && colonnaMax != null) {
    vincitaMax = Math.round(quotaMax * colonnaMax * 100) / 100;
  }
  const seVinconoTutte = piuColonne && !integrale ? (Number(g.potentialWinMax) || null) : null;

  const tetto = numeroOppureNull(g.tettoVincita);
  const piuAlta = vincitaMax != null ? vincitaMax : (seVinconoTutte || vincitaMin);
  const tettoRaggiunto = tetto != null && tetto > 0 && piuAlta != null && piuAlta >= tetto - 0.005;

  return {
    codice: g.ticketCode || '',
    cliente: clienteDi(g, io, nomeUtente),
    mia: !!io && g.userId === io,
    giocataIl: g.createdAt || null,
    ultimoEvento: g.lastEventDate || null,
    tipo,
    tipoNome,
    grandezze,
    stato: g.status || '',
    statoNome: etichettaStato(g),
    eventi,
    quanteEventi,
    posta: Number(g.stake) || 0,
    sistema: tipo === 'SYSTEM' ? sistema : [],
    piuColonne,
    colonne: combinazioni,
    colonnaMin,
    colonnaMax,
    quotaMin,
    quotaMax,
    bonusMin: Number(g.bonusMin) || 0,
    bonusMax: Number(g.bonusMax) || 0,
    vincitaMin,
    vincitaMax,
    seVinconoTutte,
    tetto: tettoRaggiunto ? tetto : null,
  };
}

// ---------------------------------------------------------------------------
// Il logo, sul rotolo e sull'A4
// ---------------------------------------------------------------------------

/* Il logo solo se chi stampa l'ha chiesto (e il servizio l'ha concesso) e la
   skin ne ha uno; se non arriva, finestraDiStampa scrive al suo posto il
   dominio di data-alt. Vuoto, se non c'e'. */
function immagineDelLogo({conLogo, skin, dominio}) {
  const logo = conLogo && skin && skin.logoUrl ? indirizzoImmagine(skin.logoUrl) : '';
  if (!logo) return '';
  return '<img class="logo" src="' + pulisci(logo) + '" alt="' + pulisci(dominio)
    + '" data-alt="' + pulisci(dominio) + '">';
}

/* LE COLONNE di un'integrale (e di ogni giocata a piu' colonne che non e' un
   sistema: il sistema le scrive nella sua tabella): quante, e quanto costa
   ognuna. La moltiplicazione solo quando e' vera, come nella tabella del
   sistema. Vuoto se non c'e' niente da dire. */
function colonneDi(d, codice) {
  if (!d.piuColonne || d.tipo === 'SYSTEM' || !(d.colonne > 1)) return '';
  if (d.colonnaMin == null || d.colonnaMax == null) return String(d.colonne);
  if (d.colonnaMin === d.colonnaMax) return `${d.colonne} \u00d7 ${soldi(d.colonnaMin, codice)}`;
  return t('{{n}}, da {{min}} a {{max}}', {
    n: d.colonne, min: soldi(d.colonnaMin, codice), max: soldi(d.colonnaMax, codice)});
}

// ---------------------------------------------------------------------------
// Il rotolo
// ---------------------------------------------------------------------------

/* IL LOGO SUL ROTOLO: in cima, al centro, alto 14 mm e largo al piu' quanto
   la carta (72 o 48 mm); in grigi, perche' la termica stampa solo il nero.
   Se l'immagine non arriva, finestraDiStampa scrive il dominio
   (.senza-immagine) e lo si scrive come la testa del rotolo. */
const STILE_LOGO_ROTOLO = '.marchio{align-self:stretch;display:flex;justify-content:center;align-items:center;padding:0 0 .5em}'
  + '.marchio .logo{display:block;width:auto;max-width:100%;height:14mm;object-fit:contain;'
    + 'filter:grayscale(1) contrast(1.2)}'
  + '.marchio .senza-immagine{font-weight:700;font-size:1.167em;text-align:center}';

/* Le misure del Rotolo A: il disegno e' a 12 px su 80 mm; su 58 mm tutto
   scende in proporzione (10 px), perche' le misure sono in em. */
const STILE_ROTOLO = '@page{margin:0}'
  + '*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}'
  + 'html,body{margin:0;padding:0;background:#fff;color:#000}'
  + '.carta{position:relative;overflow:hidden;width:72mm;margin:0 auto;padding:.83em 0 1.5em;'
    + 'display:flex;flex-direction:column;font-family:"Helvetica Neue",Arial,sans-serif;'
    + 'font-size:12px;line-height:1.25;font-variant-numeric:tabular-nums;overflow-wrap:anywhere}'
  + '.r58 .carta{width:48mm;font-size:10px}'
  + '.filigrana{position:absolute;left:0;right:0;top:43%;text-align:center;transform:rotate(-32deg);'
    + 'font-size:5.33em;line-height:1;font-weight:800;letter-spacing:.08em;color:rgba(0,0,0,.12);pointer-events:none}'
  + '.riga{display:flex;justify-content:space-between;align-items:baseline;gap:.5em}'
  + '.riga>span:last-child{text-align:right}'
  + '.testa{padding:0 0 .5em;font-weight:700}'
  + '.fascia{padding:.5em .33em;background:#000;color:#fff;text-align:center;font-size:1.083em;'
    + 'font-weight:800;letter-spacing:.04em}'
  + '.fascia.tipo{margin-top:.67em;padding:.42em .33em;letter-spacing:.06em}'
  + '.fascia.piccola{margin-top:.67em;padding:.33em;font-size:1em;letter-spacing:.06em}'
  + '.dati{border-bottom:2px solid #000}'
  + '.dati .riga{padding:.42em .17em;border-bottom:1px solid #000}'
  + '.dati .riga:last-child{border-bottom:0}'
  + '.dati .riga>span:last-child{font-weight:700}'
  + '.dati .codice{font-size:1.25em;font-weight:800;letter-spacing:.08em}'
  + '.ev{display:flex;flex-direction:column;gap:1px;padding:.5em .17em;border-bottom:1px dashed #000}'
  + '.ev:last-of-type{border-bottom:0}'
  + '.ev .su{font-weight:700}'
  + '.ev .nome{font-size:1.083em;font-weight:800}'
  + '.ev .giu{font-weight:800}'
  + '.orari{padding:.17em .17em .42em;font-size:.83em}'
  + '.puntata{padding:.5em .17em;border-top:2px solid #000;border-bottom:1px solid #000;font-size:1.083em;font-weight:800}'
  + 'table{width:100%;margin-top:.5em;border-collapse:collapse;text-align:center}'
  /* NELLE CELLE si va a capo fra le parole, non dentro («Doppie» intera):
     con «anywhere» la tabella crede che ogni colonna si possa stringere a una
     lettera. «break-word» spezza solo una parola che non ci sta. */
  + 'th,td{border:1px solid #000;padding:.33em .17em;font-weight:400;overflow-wrap:break-word}'
  + 'th{font-weight:700}'
  /* LA TABELLA DEL SISTEMA un po' piu' piccola, e le cifre della vincita su
     una riga sola: cosi' ogni riga resta bassa (chiesto dal titolare). */
  + '.sistema{font-size:.92em}'
  + '.sistema .v{white-space:nowrap}'
  + '.r58 .sistema{font-size:.84em}'
  + '.r58 .sistema th,.r58 .sistema td{padding:.33em .1em}'
  + '.quote{margin-top:.5em;padding:.5em .17em;border-top:2px solid #000;border-bottom:2px solid #000;font-weight:800}'
  + '.puntata+.quote{margin-top:0;border-top:0}'
  + '.quante-colonne{padding:.42em .17em;border-bottom:1px solid #000;font-weight:800}'
  + '.quante-colonne+.quote{margin-top:0;border-top:0}'
  + '.due{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));padding:.5em 0;text-align:center}'
  + '.uno{display:flex;flex-direction:column;align-items:center;gap:2px;padding:.5em 0;text-align:center}'
  + '.due>div{display:flex;flex-direction:column;gap:2px}'
  + '.etichetta{font-weight:700}'
  + '.cifra{font-size:1.167em;font-weight:800}'
  + '.tutte{display:flex;flex-direction:column;align-items:center;gap:2px;padding:.5em 0 .67em;text-align:center}'
  + '.tutte .cifra{font-size:1.417em;line-height:1.18}'
  + '.tetto{padding:0 .17em .67em;text-align:center;font-weight:700}'
  + '.rg{margin-top:.17em;border:2px solid #000;display:flex;flex-direction:column}'
  + '.rg .titolo{padding:.33em;border-bottom:1px solid #000;text-align:center;font-weight:800}'
  + '.rg .testo{padding:.5em .67em;display:flex;flex-direction:column;gap:3px;text-align:center}'
  + '.rg .testo b{font-weight:800}'
  + STILE_LOGO_ROTOLO;

function rigaDue(sinistra, destra, classe) {
  return '<div class="riga' + (classe ? ' ' + classe : '') + '"><span>' + pulisci(sinistra)
    + '</span><span>' + pulisci(destra) + '</span></div>';
}

function decorata(testo) {
  return '//// ' + testo + ' ////';
}

function tabellaSistemaRotolo(d, codice) {
  if (!d.sistema.length) return '';
  /* «VINC. POT.» IN ALTO E SOTTO LA CASELLA DIVISA IN DUE, «MIN.» | «MAX»,
     con una riga verticale in mezzo: due colonne vere, una cifra per cella
     (disegnato dal titolare il 2026-11-03). Le altre tre intestazioni
     occupano le due righe. */
  const th = (testo, piu) => '<th' + (piu || '') + '>' + pulisci(maiuscole(testo)) + '</th>';
  const testa = '<thead><tr>'
    + th(t('Tipo'), ' scope="col" rowspan="2"')
    + th(t('Comb.'), ' scope="col" rowspan="2"')
    + th(t('Giocato'), ' scope="col" rowspan="2"')
    + th(t('Vinc. pot.'), ' scope="colgroup" colspan="2"')
    + '</tr><tr>'
    + th(t('Min.'), ' scope="col"')
    + th(t('Max'), ' scope="col"')
    + '</tr></thead>';
  const righe = d.sistema.map((r) => {
    const comb = r.unSoloImporto
      ? `${r.combinazioni} \u00d7 ${cifra(r.colonnaMin, codice)}` : String(r.combinazioni);
    const giocato = r.unSoloImporto
      ? pulisci(cifra(r.totale, codice))
      : pulisci(cifra(r.totale, codice)) + '<br>' + pulisci(t('da {{min}} a {{max}}',
        {min: cifra(r.colonnaMin, codice), max: cifra(r.colonnaMax, codice)}));
    const scritta = (n) => pulisci(n != null ? cifra(n, codice) : '\u2014');
    /* Minimo e massimo uguali: una sola cifra al centro delle due colonne. */
    const vincita = r.vincitaMin != null && r.vincitaMin === r.vincitaMax
      ? '<td class="v" colspan="2">' + scritta(r.vincitaMin) + '</td>'
      : '<td class="v">' + scritta(r.vincitaMin) + '</td><td class="v">' + scritta(r.vincitaMax) + '</td>';
    return '<tr><td>' + pulisci(r.nome) + '</td><td>' + pulisci(comb) + '</td><td>' + giocato
      + '</td>' + vincita + '</tr>';
  }).join('');
  return '<table class="sistema">' + testa + '<tbody>' + righe + '</tbody></table>';
}

function quoteRotolo(d) {
  if (d.quotaMin == null && d.quotaMax == null) return '';
  const min = d.quotaMin != null ? d.quotaMin : d.quotaMax;
  const max = d.quotaMax != null ? d.quotaMax : d.quotaMin;
  if (min === max) return rigaDue(maiuscole(t('Quota totale')), mostraQuota(min), 'quote');
  return rigaDue(maiuscole(t('Quota min / max')), `${mostraQuota(min)} / ${mostraQuota(max)}`, 'quote');
}

function coppia(etichetta1, valore1, etichetta2, valore2) {
  return '<div class="due"><div><span class="etichetta">' + pulisci(maiuscole(etichetta1)) + '</span>'
    + '<span class="cifra">' + pulisci(valore1) + '</span></div>'
    + '<div><span class="etichetta">' + pulisci(maiuscole(etichetta2)) + '</span>'
    + '<span class="cifra">' + pulisci(valore2) + '</span></div></div>';
}

function bonusRotolo(d, codice) {
  /* Le giocate di prima del 3 ottobre 2026 hanno il bonus a zero perche'
     nessuno lo scriveva (ticketsApi): una riga «0,00» direbbe il falso. */
  if (!d.bonusMin && !d.bonusMax) return '';
  const testa = '<div class="fascia piccola">' + pulisci(maiuscole(t('Bonus'))) + '</div>';
  // Uguali (multipla, singola): una cifra sola, come la vincita.
  if (d.bonusMin === d.bonusMax) {
    return testa + '<div class="uno"><span class="cifra">'
      + pulisci(decorata(soldi(d.bonusMax, codice))) + '</span></div>';
  }
  return testa + coppia(t('Minimo'), decorata(soldi(d.bonusMin, codice)),
    t('Massimo'), decorata(soldi(d.bonusMax, codice)));
}

function vincitaRotolo(d, codice) {
  const max = d.vincitaMax == null ? '\u2014' : decorata(soldi(d.vincitaMax, codice));
  let blocco = '<div class="fascia piccola">' + pulisci(maiuscole(t('Vincita potenziale'))) + '</div>';
  if (d.vincitaMax != null && d.vincitaMax === d.vincitaMin) {
    blocco += '<div class="uno"><span class="cifra">' + pulisci(max) + '</span></div>';
  } else {
    blocco += coppia(t('Minima'), decorata(soldi(d.vincitaMin, codice)), t('Massima'), max);
  }
  if (d.seVinconoTutte != null) {
    blocco += '<div class="tutte"><span class="etichetta">'
      + pulisci(maiuscole(t('Se vincono tutte le combinazioni'))) + '</span>'
      + '<span class="cifra">' + pulisci(decorata(soldi(d.seVinconoTutte, codice))) + '</span></div>';
  }
  if (d.tetto != null) {
    blocco += '<div class="tetto">' + pulisci(t('Vincita limitata al tetto di {{tetto}}',
        {tetto: soldi(d.tetto, codice)})) + '</div>';
  }
  return blocco;
}

function eventiRotolo(d, zona) {
  return d.eventi.map((e) => '<div class="ev">'
    + rigaDue(e.competizione || '\u2014', formattaOra(e.inizio, 'completa', {fuso: zona}), 'su')
    + '<span class="nome">' + pulisci([e.numero, e.evento].filter(Boolean).join(' \u00b7 ')) + '</span>'
    + '<span>' + pulisci(e.mercato) + '</span>'
    + rigaDue(t('Segno: {{segno}}', {segno: e.segno}), t('Quota: {{quota}}', {quota: e.quota}), 'giu')
    + '</div>').join('');
}

function rotoloGiocata(d, {formato, conLogo, copia, skin, dominio, codiceValuta, testi, zona, adesso}) {
  /* Col logo, il logo prende il posto del dominio scritto: sopra, al centro,
     e la riga sotto porta solo il numero della copia. */
  const logo = immagineDelLogo({conLogo, skin, dominio});
  const intestazione = (logo ? '<div class="marchio">' + logo + '</div>' : '')
    + (logo && copia == null ? ''
      : '<div class="riga testa"><span>' + (logo ? '' : pulisci(dominio)) + '</span>'
      + (copia != null ? '<span>' + pulisci(t('copia n. {{copia}}', {copia})) + '</span>' : '<span></span>')
      + '</div>');
  const dati = '<div class="dati">'
    + '<div class="riga"><span>' + pulisci(maiuscole(t('Codice coupon'))) + '</span>'
      + '<span class="codice">' + pulisci(d.codice) + '</span></div>'
    + rigaDue(maiuscole(t('Cliente')), d.cliente)
    + rigaDue(maiuscole(t('Data')), quandoConFuso(d.giocataIl, zona))
    + rigaDue(maiuscole(t('Stato')), d.statoNome)
    + '</div>';
  const corpo = '<div class="filigrana" aria-hidden="true">' + pulisci(maiuscole(t('Copia'))) + '</div>'
    + intestazione
    + '<div class="fascia">' + pulisci(maiuscole(t('Promemoria giocata telematica'))) + '</div>'
    + dati
    + '<div class="fascia tipo">' + pulisci(maiuscole(d.tipoNome)) + '</div>'
    + '<div class="eventi">' + eventiRotolo(d, zona) + '</div>'
    + '<div class="orari">' + pulisci(notaOrari(zona, adesso)) + '</div>'
    + rigaDue(t('Puntata'), soldi(d.posta, codiceValuta), 'puntata')
    + (colonneDi(d, codiceValuta) ? rigaDue(maiuscole(t('Colonne')), colonneDi(d, codiceValuta), 'quante-colonne') : '')
    + tabellaSistemaRotolo(d, codiceValuta)
    + quoteRotolo(d)
    + bonusRotolo(d, codiceValuta)
    + vincitaRotolo(d, codiceValuta)
    + '<div class="rg"><div class="titolo">' + pulisci(maiuscole(t('Gioca responsabilmente'))) + '</div>'
    + '<div class="testo"><b>' + pulisci(t('Il gioco è vietato ai minori di 18 anni.')) + '</b>'
    + '<span>' + pulisci(t('Promemoria della giocata registrata sul conto: vale la giocata nella Lista scommesse.')) + '</span>'
    + (testi.responsabile ? '<span>' + pulisci(testi.responsabile) + '</span>' : '')
    + '</div></div>';
  return pagina({
    titolo: t('Giocata {{codice}}', {codice: d.codice}),
    stile: STILE_ROTOLO,
    classe: formato === 'ROTOLO_58' ? 'r58' : 'r80',
    corpo: '<div class="carta">' + corpo + '</div>',
  });
}

// ---------------------------------------------------------------------------
// L'A4
// ---------------------------------------------------------------------------

/* Il numero di pagina lo scrive il browser nel margine (@page, dove lo sa
   fare): una giocata di quaranta eventi esce su due fogli, e «pagina 1 di 1»
   scritto a mano sarebbe falso sul secondo. */
function numeroDiPagina() {
  /* Due segnaposto dell'area privata di Unicode, che nessuna traduzione scrive. */
  const N = '\ue000';
  const TOTALE = '\ue001';
  const modello = t('pagina {{n}} di {{totale}}', {n: N, totale: TOTALE});
  const pezzi = modello.split(/(\ue000|\ue001)/).filter((p) => p !== '').map((p) => {
    if (p === N) return 'counter(page)';
    if (p === TOTALE) return 'counter(pages)';
    /* Anche «<» si scrive come codice CSS: una traduzione con «</style>»
       chiuderebbe lo stile e finirebbe nella pagina. */
    return '"' + p.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/[\r\n]/g, ' ')
      .replace(/</g, '\\3c ') + '"';
  });
  return '@page{size:A4;margin:12mm;@bottom-right{content:' + pezzi.join(' ')
    + ';font:10px Arial,Helvetica,sans-serif;color:#555}}';
}

const STILE_A4 = '*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}'
  + 'html,body{margin:0;padding:0;background:#fff;color:#111}'
  + '.carta{display:flex;flex-direction:column;gap:22px;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif;'
    + 'font-variant-numeric:tabular-nums;overflow-wrap:anywhere}'
  + '.testa{display:flex;align-items:flex-start;justify-content:space-between;gap:24px;padding-bottom:16px;border-bottom:2px solid #111}'
  + '.logo{max-width:180px;max-height:56px;object-fit:contain}'
  + '.dominio,.senza-immagine{font-size:20px;font-weight:700;letter-spacing:.02em}'
  + '.dx{display:flex;flex-direction:column;align-items:flex-end;gap:4px;text-align:right}'
  + '.copia{font-size:12px;font-weight:700;letter-spacing:.12em;text-transform:uppercase}'
  + '.grigio{font-size:13px;color:#444}'
  + '.blocco-codice{display:flex;flex-direction:column;gap:6px}'
  + '.etichetta{font-size:13px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#444}'
  + '.grande{font-family:ui-monospace,"SF Mono",Menlo,Consolas,monospace;font-size:40px;font-weight:700;letter-spacing:.12em}'
  + '.sotto{font-size:16px;color:#333}'
  + '.colonne{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:32px}'
  + 'section{display:flex;flex-direction:column;gap:6px}'
  + 'h2{margin:0 0 4px;font-size:13px;font-weight:700;letter-spacing:.1em;text-transform:uppercase}'
  + '.voce{display:flex;justify-content:space-between;gap:12px;padding:6px 0;border-bottom:1px solid #bbb;font-size:15px}'
  + '.voce:last-child{border-bottom:0}'
  + '.voce>span:first-child{color:#444}'
  + '.voce.forte{align-items:baseline;padding-top:8px}'
  + '.voce.forte>span:first-child{color:#111;font-weight:700}'
  + '.voce.forte>span:last-child{font-size:24px;font-weight:700}'
  + 'table{width:100%;border-collapse:collapse;font-size:14px}'
  + 'th,td{overflow-wrap:break-word}'
  + 'thead tr{border-top:1px solid #111;border-bottom:1px solid #111}'
  + 'th{text-align:left;padding:7px 6px;font-size:12px;text-transform:uppercase;letter-spacing:.06em}'
  + 'td{padding:8px 6px;vertical-align:top;border-bottom:1px solid #bbb}'
  + 'tbody tr:last-child td{border-bottom:1px solid #111}'
  + '.num{text-align:right}'
  + 'td.forte{font-weight:700}'
  + '.comp{font-size:12px;color:#444}'
  + '.nota{font-size:12px;color:#444}'
  + 'tr{page-break-inside:avoid}'
  + '.piede{display:flex;flex-direction:column;gap:8px;padding-top:14px;border-top:1px solid #111;font-size:12px;line-height:17px;color:#333}'
  + '.piede .cortesia{font-size:13px;font-weight:700;color:#111}'
  + '.piede .fondo{color:#555}';

function voce(etichetta, valore, classe) {
  return '<div class="voce' + (classe ? ' ' + classe : '') + '"><span>' + pulisci(etichetta)
    + '</span><span>' + pulisci(valore) + '</span></div>';
}

function testaA4({conLogo, skin, dominio, destra}) {
  const sinistra = immagineDelLogo({conLogo, skin, dominio})
    || '<div class="dominio">' + pulisci(dominio) + '</div>';
  return '<header class="testa">' + sinistra + '<div class="dx">' + destra + '</div></header>';
}

function tabellaEventiA4(eventi, zona) {
  const testa = '<thead><tr>'
    /* Ogni parola nel suo t('…'): la raccolta delle parole (scripts/
       raccogli-parole.mjs) vede solo le chiavi scritte, non t(c). */
    + [t('N.'), t('Evento'), t('Inizio'), t('Mercato'), t('Segno')].map((c) =>
      '<th scope="col">' + pulisci(c) + '</th>').join('')
    + '<th scope="col" class="num">' + pulisci(t('Quota')) + '</th></tr></thead>';
  const righe = eventi.map((e) => '<tr>'
    + '<td>' + pulisci(e.numero) + '</td>'
    + '<td><b>' + pulisci(e.evento || '\u2014') + '</b>'
      + (e.competizione ? '<br><span class="comp">' + pulisci(e.competizione) + '</span>' : '') + '</td>'
    + '<td>' + pulisci(formattaOra(e.inizio, 'giorno-ora', {fuso: zona})) + '</td>'
    + '<td>' + pulisci(e.mercato) + '</td>'
    + '<td class="forte">' + pulisci(e.segno) + '</td>'
    + '<td class="num forte">' + pulisci(e.quota) + '</td></tr>').join('');
  return '<table>' + testa + '<tbody>' + righe + '</tbody></table>';
}

function tabellaSistemaA4(d, codice) {
  if (!d.sistema.length) return '';
  const testa = '<thead><tr>'
    + [t('Tipo'), t('Combinazioni'), t('Giocato')].map((c) => '<th scope="col">' + pulisci(c) + '</th>').join('')
    + '<th scope="col" class="num">' + pulisci(t('Vincita minima')) + '</th>'
    + '<th scope="col" class="num">' + pulisci(t('Vincita massima')) + '</th></tr></thead>';
  const righe = d.sistema.map((r) => {
    const giocato = r.unSoloImporto
      ? `${r.combinazioni} \u00d7 ${soldi(r.colonnaMin, codice)} = ${soldi(r.totale, codice)}`
      : t('{{totale}} su {{n}} colonne (da {{min}} a {{max}})', {
        totale: soldi(r.totale, codice), n: r.combinazioni,
        min: soldi(r.colonnaMin, codice), max: soldi(r.colonnaMax, codice)});
    return '<tr><td>' + pulisci(r.nome) + '</td><td>' + pulisci(r.combinazioni) + '</td>'
      + '<td>' + pulisci(giocato) + '</td>'
      + '<td class="num">' + pulisci(r.vincitaMin != null ? soldi(r.vincitaMin, codice) : '\u2014') + '</td>'
      + '<td class="num">' + pulisci(r.vincitaMax != null ? soldi(r.vincitaMax, codice) : '\u2014') + '</td></tr>';
  }).join('');
  return '<section><h2>' + pulisci(t('Sistema')) + '</h2><table>' + testa + '<tbody>' + righe + '</tbody></table></section>';
}

function scriviEventi(n) {
  return n === 1 ? t('1 evento') : t('{{n}} eventi', {n});
}

function piedeA4(testi, dominio, cortesia) {
  return '<footer class="piede">'
    + '<span class="cortesia">' + pulisci(cortesia) + '</span>'
    + (testi.legale ? '<span>' + pulisci(testi.legale) + '</span>' : '')
    + '<span>' + pulisci('18+ \u00b7 ' + (testi.responsabile || '')) + '</span>'
    + '<span class="fondo">' + pulisci(dominio) + '</span>'
    + '</footer>';
}

function a4Giocata(d, {conLogo, copia, skin, dominio, codiceValuta, testi, zona, adesso}) {
  const destra = '<span class="copia">' + pulisci(copia != null
    ? t('Copia della giocata · copia n. {{copia}}', {copia}) : t('Copia della giocata')) + '</span>'
    + '<span class="grigio">' + pulisci(t('stampata il {{quando}}', {quando: quandoConFuso(adesso, zona)})) + '</span>';

  const quote = (() => {
    if (d.quotaMin == null && d.quotaMax == null) return '';
    const min = d.quotaMin != null ? d.quotaMin : d.quotaMax;
    const max = d.quotaMax != null ? d.quotaMax : d.quotaMin;
    return min === max ? voce(t('Quota totale'), mostraQuota(min))
      : voce(t('Quota minima e massima'), `${mostraQuota(min)} / ${mostraQuota(max)}`);
  })();
  const bonus = !d.bonusMin && !d.bonusMax ? ''
    : voce(t('Bonus'), d.bonusMin === d.bonusMax ? soldi(d.bonusMax, codiceValuta)
      : `${soldi(d.bonusMin, codiceValuta)} \u2013 ${soldi(d.bonusMax, codiceValuta)}`);
  /* Il massimo di una colonna che non si sa: «da 11,50 €», non «11,50 € – —». */
  const vincita = d.vincitaMax == null
    ? t('da {{min}}', {min: soldi(d.vincitaMin, codiceValuta)})
    : (d.vincitaMax === d.vincitaMin ? soldi(d.vincitaMax, codiceValuta)
      : `${soldi(d.vincitaMin, codiceValuta)} \u2013 ${soldi(d.vincitaMax, codiceValuta)}`);

  const corpo = testaA4({conLogo, skin, dominio, destra})
    + '<div class="blocco-codice"><span class="etichetta">' + pulisci(t('Codice giocata')) + '</span>'
    + '<span class="grande">' + pulisci(d.codice) + '</span>'
    + '<span class="sotto">' + pulisci([d.tipoNome, scriviEventi(d.quanteEventi), d.statoNome].join(' \u00b7 ')) + '</span></div>'
    + '<div class="colonne">'
    + '<section><h2>' + pulisci(t('La giocata')) + '</h2>'
      + voce(t('Tipo'), d.tipoNome)
      + voce(t('Giocata il'), quandoConFuso(d.giocataIl, zona))
      + voce(t('Ultimo evento'), d.ultimoEvento ? quandoConFuso(d.ultimoEvento, zona) : '\u2014')
      + voce(t('Stato'), d.statoNome) + '</section>'
    + '<section><h2>' + pulisci(t('I numeri')) + '</h2>'
      + voce(t('Posta'), soldi(d.posta, codiceValuta))
      + (colonneDi(d, codiceValuta) ? voce(t('Colonne'), colonneDi(d, codiceValuta)) : '')
      + quote + bonus
      + (d.seVinconoTutte != null ? voce(t('Se vincono tutte le combinazioni'), soldi(d.seVinconoTutte, codiceValuta)) : '')
      + (d.tetto != null ? voce(t('Tetto della vincita'), soldi(d.tetto, codiceValuta)) : '')
      + voce(t('Vincita potenziale'), vincita, 'forte') + '</section>'
    + '</div>'
    + '<section><h2>' + pulisci(t('Eventi')) + '</h2>' + tabellaEventiA4(d.eventi, zona)
    + '<span class="nota">' + pulisci(notaOrari(zona, adesso)) + '</span></section>'
    + tabellaSistemaA4(d, codiceValuta)
    + piedeA4(testi, dominio, t('Copia di cortesia: vale la giocata registrata nella Lista scommesse del conto.'));

  return pagina({
    titolo: t('Giocata {{codice}}', {codice: d.codice}),
    stile: numeroDiPagina() + STILE_A4,
    classe: 'a4',
    corpo: '<div class="carta">' + corpo + '</div>',
  });
}

// ---------------------------------------------------------------------------
// La pagina intera
// ---------------------------------------------------------------------------

function pagina({titolo, stile, classe, corpo}) {
  return '<!DOCTYPE html><html lang="' + pulisci(lingua()) + '"><head><meta charset="utf-8">'
    + '<title>' + pulisci(titolo) + '</title><style>' + stile + '</style></head>'
    + '<body class="' + pulisci(classe) + '">' + corpo + '</body></html>';
}

function contesto({skin, testi, adesso, fuso}) {
  const s = skin || skinCorrente() || {};
  return {
    skin: s,
    dominio: s.domain || s.name || '',
    codiceValuta: s.currency || valuta(),
    testi: testi || testiStampa(),
    adesso: adesso instanceof Date ? adesso : new Date(adesso || Date.now()),
    zona: fuso || fusoDiChiGuarda(),
  };
}

/**
 * LA GIOCATA, pagina intera da mandare a stampaPagina.
 *
 * @param giocata  una riga di ticketsApi (o `dati`, gia' passata da datiStampa)
 * @param formato  'A4' | 'ROTOLO_80' | 'ROTOLO_58': quello che ha risposto il servizio
 * @param conLogo  quello che ha risposto il servizio (sull'A4 e sul rotolo)
 * @param copia    il numero di copia dato dal servizio
 * @param io, nomeUtente  per il CLIENTE (datiStampa)
 * @param skin, testi, adesso, fuso  di serie: la skin, i testi legali, ora, fuso()
 */
export function paginaGiocata({giocata, dati, io, nomeUtente, formato, conLogo, copia, skin, testi, adesso, fuso} = {}) {
  const d = dati || datiStampa(giocata, {io, nomeUtente});
  const f = formatoDi(formato);
  const c = contesto({skin, testi, adesso, fuso});
  const n = copia == null ? null : copia;
  if (f === 'A4') return a4Giocata(d, {...c, conLogo: !!conLogo, copia: n});
  return rotoloGiocata(d, {...c, formato: f, conLogo: !!conLogo, copia: n});
}

// ---------------------------------------------------------------------------
// La prenotazione
// ---------------------------------------------------------------------------

const DAL_TIPO_DELLA_SCHEDINA = {
  singola: 'SINGLE', multipla: 'MULTIPLE', sistema: 'SYSTEM', integrale: 'INTEGRAL',
};

const TIPI = ['SINGLE', 'MULTIPLE', 'SYSTEM', 'INTEGRAL'];

/* Il tipo arriva con le parole del ticket (la Vista: MULTIPLE) o con quelle
   della schedina (il riepilogo: 'multipla'). Una multipla di un evento solo
   e' una singola. */
function tipoPrenotazione(tipo, quante) {
  const scritto = String(tipo || '');
  let chiave = TIPI.includes(scritto) ? scritto : DAL_TIPO_DELLA_SCHEDINA[scritto.toLowerCase()];
  if (!chiave) chiave = 'MULTIPLE';
  if (chiave === 'MULTIPLE' && quante === 1) chiave = 'SINGLE';
  return chiave;
}

/**
 * Una prenotazione come la scrivono carta, immagine e messaggio.
 *
 * Accetta la Vista del servizio (codice, creataIl, scadeIl, tipo, importo,
 * vincitaVista, selezioni[] con eventName, competitionName, inizio,
 * marketName, selectionName, sbv, prezzo) piu' `link`; dove manca qualcosa
 * vale il `riepilogo` che la schedina tiene dopo aver prenotato ({quante,
 * tipo, importo, vincita}). Un importo nullo o zero e' «nessuna proposta»:
 * la prenotazione nata da una giocata condivisa non porta la posta.
 */
export function datiPrenotazione(prenotazione) {
  const p = prenotazione || {};
  const r = p.riepilogo || {};
  const selezioni = Array.isArray(p.selezioni) ? p.selezioni : [];
  const eventi = selezioni.map((s) => {
    const prezzo = numeroOppureNull(s.prezzo);
    return {
      competizione: s.competitionName || '',
      inizio: s.inizio || s.startsAt || null,
      numero: s.eventNumber != null ? String(s.eventNumber) : '',
      evento: s.eventName || s.matchName || '',
      mercato: s.marketName || s.marketKey || '-',
      segno: segnoDi(s),
      quota: prezzo != null ? (mostraQuota(prezzo) || '-') : '-',
      valore: prezzo,
    };
  });
  /* «N eventi» sono le PARTITE, non le selezioni: un'integrale con due segni
     sulla stessa partita e' un evento solo (come eventsCount sulla giocata). */
  const conPartita = selezioni.every((s) => s.eventId);
  const quante = (conPartita ? new Set(selezioni.map((s) => s.eventId)).size : eventi.length)
    || Number(r.quante) || 0;
  const tipo = tipoPrenotazione(p.tipo || r.tipo, quante);
  const importo = numeroOppureNull(p.importo != null ? p.importo : r.importo);
  const vincita = numeroOppureNull(p.vincitaVista != null ? p.vincitaVista : r.vincita);
  /* LA QUOTA solo per singola e multipla, prodotto dei prezzi: un'integrale
     non ha una quota sola. */
  const quota = (tipo === 'SINGLE' || tipo === 'MULTIPLE') && eventi.length
      && eventi.every((e) => e.valore != null && e.valore > 0)
    ? eventi.reduce((x, e) => x * e.valore, 1) : null;
  return {
    codice: p.codice || '',
    link: p.link || '',
    creataIl: p.creataIl || null,
    scadeIl: p.scadeIl || null,
    tipo,
    tipoNome: typeLabels[tipo] || '',
    quante,
    eventi,
    importo: importo != null && importo > 0 ? importo : null,
    vincita: vincita != null && vincita > 0 ? vincita : null,
    quota,
  };
}

const STILE_PRENOTAZIONE_ROTOLO = '@page{margin:0}'
  + '*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}'
  + 'html,body{margin:0;padding:0;background:#fff;color:#000}'
  + '.carta{width:72mm;margin:0 auto;padding:1.5em 0 2em;display:flex;flex-direction:column;gap:.83em;'
    + 'font-family:ui-monospace,"SF Mono",Menlo,Consolas,"Courier New",monospace;font-size:12px;line-height:1.33;'
    + 'font-variant-numeric:tabular-nums;overflow-wrap:anywhere}'
  + '.r58 .carta{width:48mm;font-size:10px}'
  + '.centro{display:flex;flex-direction:column;align-items:center;gap:.33em;text-align:center}'
  + '.forte{font-weight:700}'
  + '.titolo{font-size:1.33em;font-weight:700;letter-spacing:.12em}'
  + '.codice{font-size:2.17em;line-height:1.15;font-weight:700;letter-spacing:.14em}'
  + '.riga{display:flex;justify-content:space-between;gap:.67em}'
  + '.riga>span:last-child{text-align:right}'
  + '.ev{display:flex;flex-direction:column;gap:1px}'
  + '.linea{border-top:2px solid #000}'
  + '.tratto{border-top:1px dashed #000}'
  + '.somma{display:flex;flex-direction:column;gap:2px}'
  + '.taglio{display:flex;align-items:center;gap:.5em;color:#555;margin-top:.5em}'
  + '.taglio>span.t{flex-grow:1;border-top:1px dashed #555}'
  + STILE_LOGO_ROTOLO;

function eventiPrenotazioneRotolo(d, zona) {
  return d.eventi.map((e) => '<div class="tratto"></div><div class="ev">'
    + rigaDue(e.numero || e.competizione, formattaOra(e.inizio, 'giorno-ora', {fuso: zona}))
    + '<span class="forte">' + pulisci(e.evento || '\u2014') + '</span>'
    + rigaDue(`${e.mercato} \u00b7 ${e.segno}`, e.quota)
    + '</div>').join('');
}

function sommaPrenotazione(d, codice, Riga) {
  let s = Riga(d.tipoNome, scriviEventi(d.quante));
  /* L'IMPORTO E' LA PROPOSTA STESSA: chi prenota scrive quanto giocherebbe e
     il messaggio lo porta da sempre. La regola degli importi nascosti vale
     per la giocata condivisa, non qui. Senza importo non si scrive niente. */
  if (d.importo != null) {
    s += Riga(t('Importo proposto'), soldi(d.importo, codice));
    if (d.quota != null) s += Riga(t('Quota'), mostraQuota(d.quota));
    if (d.vincita != null) s += Riga(t('Vincita indicativa'), soldi(d.vincita, codice), 'forte');
  }
  return s;
}

function rotoloPrenotazione(d, {formato, conLogo, skin, dominio, codiceValuta, zona}) {
  const logo = immagineDelLogo({conLogo, skin, dominio});
  const corpo = '<div class="centro">'
    + (logo ? '<div class="marchio">' + logo + '</div>' : '<span class="forte">' + pulisci(dominio) + '</span>')
    + '<span class="titolo">' + pulisci(maiuscole(t('Prenotazione'))) + '</span>'
    + '<span>' + pulisci(t('Non è una giocata: si gioca col codice, alle quote del momento.')) + '</span>'
    + '</div>'
    + '<div class="linea"></div>'
    + '<div class="centro">'
    + '<span>' + pulisci(maiuscole(t('Codice'))) + '</span>'
    + '<span class="codice">' + pulisci(d.codice) + '</span>'
    + (d.link ? '<span>' + pulisci(d.link) + '</span>' : '')
    + (d.creataIl ? '<span>' + pulisci(t('Creata {{quando}}', {quando: quandoConFuso(d.creataIl, zona)})) + '</span>' : '')
    + (d.scadeIl ? '<span class="forte">' + pulisci(t('Vale fino al {{quando}}', {quando: quandoConFuso(d.scadeIl, zona)})) + '</span>' : '')
    + '</div>'
    + eventiPrenotazioneRotolo(d, zona)
    + '<div class="linea"></div>'
    + '<div class="somma">' + sommaPrenotazione(d, codiceValuta, rigaDue) + '</div>'
    + '<div class="linea"></div>'
    + '<div class="centro">'
    + '<span>' + pulisci(t('Si gioca sul sito, aprendo il collegamento o scrivendo il codice nella schedina.')) + '</span>'
    + '<span>' + pulisci(t('18+ · Il gioco può causare dipendenza.')) + '</span>'
    + '</div>'
    + '<div class="taglio" aria-hidden="true"><span class="t"></span><span>' + pulisci(t('taglio'))
    + '</span><span class="t"></span></div>';
  return pagina({
    titolo: t('Prenotazione {{codice}}', {codice: d.codice}),
    stile: STILE_PRENOTAZIONE_ROTOLO,
    classe: formato === 'ROTOLO_58' ? 'r58' : 'r80',
    corpo: '<div class="carta">' + corpo + '</div>',
  });
}

function a4Prenotazione(d, {conLogo, skin, dominio, codiceValuta, testi, zona, adesso}) {
  const destra = '<span class="copia">' + pulisci(t('Prenotazione')) + '</span>'
    + '<span class="grigio">' + pulisci(t('stampata il {{quando}}', {quando: quandoConFuso(adesso, zona)})) + '</span>';
  const corpo = testaA4({conLogo, skin, dominio, destra})
    + '<div class="blocco-codice"><span class="etichetta">' + pulisci(t('Codice')) + '</span>'
    + '<span class="grande">' + pulisci(d.codice) + '</span>'
    + (d.link ? '<span class="sotto">' + pulisci(d.link) + '</span>' : '')
    + '<span class="grigio">' + pulisci(t('Non è una giocata: si gioca col codice, alle quote del momento.')) + '</span></div>'
    + '<div class="colonne">'
    + '<section><h2>' + pulisci(t('La prenotazione')) + '</h2>'
      + (d.creataIl ? voce(t('Creata'), quandoConFuso(d.creataIl, zona)) : '')
      + (d.scadeIl ? voce(t('Vale fino al'), quandoConFuso(d.scadeIl, zona)) : '')
      + '</section>'
    + '<section><h2>' + pulisci(t('I numeri')) + '</h2>'
      + sommaPrenotazione(d, codiceValuta, (a, b, classe) => voce(a, b, classe))
      + '</section>'
    + '</div>'
    + '<section><h2>' + pulisci(t('Eventi')) + '</h2>' + tabellaEventiA4(d.eventi, zona)
    + '<span class="nota">' + pulisci(notaOrari(zona, adesso)) + '</span></section>'
    + piedeA4(testi, dominio, t('Si gioca sul sito, aprendo il collegamento o scrivendo il codice nella schedina.'));
  return pagina({
    titolo: t('Prenotazione {{codice}}', {codice: d.codice}),
    stile: numeroDiPagina() + STILE_A4,
    classe: 'a4',
    corpo: '<div class="carta">' + corpo + '</div>',
  });
}

/**
 * LA PRENOTAZIONE, pagina intera da mandare a stampaPagina.
 *
 * Niente QR (codice e collegamento scritti). Il logo, sul rotolo come
 * sull'A4, c'e' quando la regola non dice MAI: chi chiama passa
 * conLogo = regole.logo !== 'MAI' (la maschera della prenotazione non ha la
 * casella «senza logo»). Le prenotazioni non si contano: non sono giocate.
 */
export function paginaPrenotazione({prenotazione, formato, conLogo, skin, testi, adesso, fuso} = {}) {
  const d = datiPrenotazione(prenotazione);
  const f = formatoDi(formato);
  const c = contesto({skin, testi, adesso, fuso});
  if (f === 'A4') return a4Prenotazione(d, {...c, conLogo: !!conLogo});
  return rotoloPrenotazione(d, {...c, formato: f, conLogo: !!conLogo});
}
